import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Outlet, useNavigate, useRouter, useRouterState } from '@tanstack/react-router';
import { LogOut, PenLine, PanelLeft, SunMoon } from 'lucide-react';
import type { RequisitionStatus } from '@eombor/contracts';
import { cn } from '@eombor/ui';
import { t } from '../../shared/i18n/index.js';
import { nextThemeMode, useThemeMode } from '../../shared/theme/useThemeMode.js';
import { useAuthStore } from '../../features/auth/model/auth.store.js';
import { usePermission } from '../../features/auth/model/usePermission.js';
import { usePendingBackorderCount } from '../../features/backorder/model/backorder.queries.js';
import { accountingApi } from '../../features/accounting/api/accounting.api.js';
import { requisitionApi } from '../../features/requisition/api/requisition.api.js';
import { useInbox } from '../../features/requisition/model/requisition.queries.js';
import { useUnreadCountOfKind } from '../../features/notification/model/notification.queries.js';
import { GlobalScanner } from './GlobalScanner.js';
import { ErrorBoundary } from '../../shared/ui/ErrorBoundary.js';

/**
 * Navbat sonlari qanchalik tez eskiradi.
 *
 * Navbatga ish BOSHQA xodimning amalidan tushadi va sahifada hech qanday
 * mahalliy hodisa bo'lmaydi. 60 soniya — "mening navbatim" va
 * bildirishnoma sanog'i bilan bir xil oraliq: smena ichida sezilarli
 * kechikish emas, serverga ham yuk emas.
 */
const QUEUE_REFETCH_MS = 60_000;
import { CommandPalette, type CommandItem } from './CommandPalette.js';
import { MySignatureDialog } from '../../features/signature/ui/MySignatureDialog.js';
import { useMySubdivision } from '../../features/subdivision/model/subdivision.queries.js';
import { Sidebar } from './Sidebar.js';
import { isNavItemActive, navEntry, visibleNavItems } from './nav-model.js';
import { MobileNavDrawer } from './MobileNavDrawer.js';
import { MobileTabBar } from './MobileTabBar.js';
import { Topbar } from './Topbar.js';
import { useMobileNav } from './useMobileNav.js';

/** Yig'ilgan holat brauzerda qoladi — har kirishda qaytadan yig'ish shart emas. */
export const SIDEBAR_STORAGE_KEY = 'eombor:sidebar';

const readCollapsed = (): boolean => {
  try {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'collapsed';
  } catch {
    // Saqlash taqiqlangan bo'lsa — panel har safar ochiq holda boshlanadi.
    return false;
  }
};

export const useSidebarCollapsed = (): [boolean, () => void] => {
  const [collapsed, setCollapsed] = useState(readCollapsed);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, collapsed ? 'collapsed' : 'expanded');
    } catch {
      // Yuqoridagi bilan bir xil sabab — bu ilovani to'xtatmaydi.
    }
  }, [collapsed]);

  return [collapsed, useCallback(() => setCollapsed((current) => !current), [])];
};

/**
 * Ilova karkasi: chapda navigatsiya, tepada boshqaruvlar, o'rtada sahifa.
 * Bir smena davomida shu uchtasi o'zgarmaydi — faqat `<Outlet />` almashadi.
 */

/**
 * `/requisitions` bo'limidagi son — TASDIQLASH KUTAYOTGAN talabnomalar.
 *
 * Ikkita chegirma bor va ikkalasi ham ataylab:
 *
 *   1. **O'z bo'limi bor holatlar chiqarib tashlanadi.** `READY_FOR_PICKUP`
 *      `/handover` da, `ACCOUNTING_PENDING` esa `/accounting/surveys` da
 *      sanaladi. Ular bu yerda ham sanalsa, skladchi bitta ishni ikki
 *      bo'limda ko'rib, qaysi ekranga borishni bilmasdi.
 *
 *   2. **Buyurtmachiga umuman ko'rsatilmaydi.** Unda «Mening navbatim»
 *      bo'limi bor va o'sha son shu yerda ikkinchi marta takrorlanardi.
 *      `requisition.create` aynan buyurtmachini ajratadi: NOD da,
 *      skladchida va buxgalterda u yo'q. *
 * Ko'rib chiqilmagan BILDIRGILAR esa har doim QO'SHILADI: ular shu bo'lim
 * ichida turadi va son faqat qabul qila oladigan odamga keladi — chegirma
 * 2 dagi takrorlanish bu yerda yo'q.
 */
const SECTIONED_STATUSES: readonly RequisitionStatus[] = ['READY_FOR_PICKUP', 'ACCOUNTING_PENDING'];

export const requisitionsBadge = (
  byStatus: Partial<Record<RequisitionStatus, number>> | undefined,
  hasInboxSection: boolean,
  pendingBackorders = 0,
): number => {
  if (byStatus === undefined || hasInboxSection) return pendingBackorders;
  return (
    Object.entries(byStatus)
      .filter(([status]) => !SECTIONED_STATUSES.includes(status as RequisitionStatus))
      .reduce((sum, [, count]) => sum + count, 0) + pendingBackorders
  );
};

export const AppShell = () => {
  const navigate = useNavigate();
  const router = useRouter();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  // Menyu `canAnywhere` bilan chiziladi, `can` bilan emas: bo'lim ochilishidan
  // oldin qaysi ombor haqida gap ketayotgani ma'lum emas, shuning uchun
  // doiraga bog'langan grant ham havolani ko'rsatishi kerak. Ichkaridagi
  // tugmalar esa `can(permission, target)` bilan tekshiriladi.
  const { canAnywhere } = usePermission();
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  const [paletteOpen, setPaletteOpen] = useState(false);
  /* Telefonda navigatsiya boshqacha chiziladi: chapdagi panel o'rniga
     pastki qator, qolgan bo'limlar esa «Menyu» ichida. */
  const mobile = useMobileNav();
  const [navMenuOpen, setNavMenuOpen] = useState(false);
  /* «Mening imzoyim» — imzoni ko'rish va almashtirish. Tasdiqlash oynasidagi
     bo'lim uni faqat BIR MARTA so'raydi, almashtirish esa shu yerdan. */
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [themeMode, setThemeMode] = useThemeMode();

  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  /* Yon paneldagi belgi — NAVBATDAGI ARIZALAR soni, o'qilmagan bildirishnoma
     soni emas. Ilgari bu yerda bildirishnomalar turardi va natijada bir xil
     "Mening navbatim" yorlig'i ostida ikki xil son ko'rinardi: yon panelda 6,
     sahifada 7. Bildirishnomalar yuqoridagi qo'ng'iroqda qoladi — ular boshqa
     narsa: bildirishnoma o'qilgan bo'lishi mumkin, talabnoma esa baribir navbatda
     turaveradi.

     So'rov karkasda turadi (sahifada emas): son har bo'limda ko'rinishi kerak,
     xodim "mening navbatim" ni ochmasa ham. Sahifa bilan bir xil `queryKey`,
     shuning uchun ikkinchi so'rov ketmaydi. */
  const inbox = useInbox();

  /* Tushum belgisi — bildirishnomadan, chunki "ish qoldi" degan savolga
     javob HUJJAT emas, xodim: bir ombordagi hisobot ikkinchi ombor
     skladchisiga tegishli emas va u ochilgach belgi o'chishi kerak.
     Ruxsati yo'q xodim uchun so'rov umuman ketmaydi. */
  const supplyUnread = useUnreadCountOfKind(
    'SUPPLY_ARRIVED',
    canAnywhere('stock.supply.place'),
  );

  /*
   * NAVBAT SONLARI — har rol o'z bo'limida.
   *
   * "Mening navbatim" ilgari yolg'iz turardi va qolgan rollar o'z ishini
   * faqat bo'limni OCHIB bilardi: skladchi berishga uchta talabnoma
   * turganini, buxgalter esa so'rovnoma kutayotganini payqamasdi.
   *
   * Ruxsati yo'q xodim uchun so'rov umuman ketmaydi (`enabled`): yon panel
   * unga bu bo'limlarni ko'rsatmaydi ham.
   */
  const toIssue = useQuery({
    queryKey: ['handover', 'ready-count'] as const,
    queryFn: async () =>
      (await requisitionApi.list({ status: 'READY_FOR_PICKUP', limit: 1 })).total,
    enabled: canAnywhere('stock.issue'),
    refetchInterval: QUEUE_REFETCH_MS,
  });

  const toApprove = useQuery({
    queryKey: ['accounting', 'pending-count'] as const,
    queryFn: async () => (await accountingApi.listSurveys({ status: 'PENDING', limit: 1 })).total,
    enabled: canAnywhere('accounting.survey.approve'),
    refetchInterval: QUEUE_REFETCH_MS,
  });

  /*
   * NAVBAT HOLAT BO'YICHA — TASDIQLOVCHI ROLLAR uchun.
   *
   * Ilgari navbat soni faqat «Mening navbatim» da ko'rinardi, u bo'lim esa
   * `requisition.create` bilan yopilgan — ya'ni FAQAT buyurtmachida. NOD
   * (`SUBMITTED` ni tasdiqlaydi) va skladchi (`ORG_APPROVED` ni qabul
   * qiladi) ishni `/requisitions` dan olishadi va u yerda hech qanday son
   * yo'q edi: ular navbatda ish borligini faqat ro'yxatni OCHIB bilishardi.
   *
   * Ruxsati yo'q xodim uchun so'rov ketaveradi va bo'sh javob qaytaradi —
   * `inbox` bilan ayni sabab: bu ro'yxat ruxsatdan hisoblanadi.
   */
  const queue = useQuery({
    queryKey: ['requisition', 'inbox-counts'] as const,
    queryFn: () => requisitionApi.inboxCounts(),
    refetchInterval: QUEUE_REFETCH_MS,
  });

  /* Yangi bildirgilar — faqat qabul qila oladiganga: rahbar kuzatadi, lekin
     qaror qilmaydi va uning menyusidagi belgi yolg'on bo'lardi. */
  const pendingBackorders = usePendingBackorderCount(canAnywhere('backorder.accept'));

  const badges = useMemo(
    () => ({
      '/inbox': inbox.data?.total ?? 0,
      '/supply': supplyUnread,
      '/handover': toIssue.data ?? 0,
      '/accounting/surveys': toApprove.data ?? 0,
      '/requisitions': requisitionsBadge(
        queue.data?.byStatus,
        canAnywhere('requisition.create'),
        pendingBackorders.data ?? 0,
      ),
    }),
    [
      inbox.data,
      supplyUnread,
      toIssue.data,
      toApprove.data,
      queue.data,
      pendingBackorders.data,
      canAnywhere,
    ],
  );

  const go = useCallback(
    (to: string): void => {
      // Marshrutlar ro'yxati `NAV_ITEMS` bilan bir manbadan quriladi, shuning
      // uchun `to` doim mavjud yo'l; `href` esa oddiy satr bo'lib qoladi.
      void navigate({ to, replace: false });
    },
    [navigate],
  );

  const handleLogout = useCallback((): void => {
    void logout();
  }, [logout]);

  /*
   * «Mening omborim» ruxsatga emas, ISH JOYIGA bog'liq: bo'lim biriktirilgan
   * xodimda bor, boshqasida yo'q. Javob kelmaguncha havola chizilmaydi —
   * paydo bo'lib, keyin yo'qolib ketishdan ko'ra kechroq paydo bo'lgani
   * yaxshiroq.
   */
  const hasSubdivision = useMySubdivision().isSuccess;

  const allowed = useMemo(
    () => visibleNavItems(canAnywhere, hasSubdivision),
    [canAnywhere, hasSubdivision],
  );

  const section = allowed.find((item) => item.to !== '/' && isNavItemActive(item.to, pathname));

  const sectionLabel = section?.label ?? (pathname === '/' ? 'nav.dashboard' : undefined);

  /*
   * ICHKI SAHIFA — manzil bo'lim BOSHIDAN chuqurroq (`/warehouses/42`).
   *
   * Telefonda qaytish yo'li kerak, chunki pastki qatordagi tugma bo'lim
   * boshiga olib boradi, bir pog'ona yuqoriga emas: javon kartochkasidan
   * ro'yxatga qaytmoqchi bo'lgan odam butun bo'limni qaytadan ochardi.
   * Brauzer tarixiga tayanamiz — sahifalar ierarxiyasi karkasda yozilgan
   * emas va uni ikkinchi marta ta'riflash ikki manba yaratardi.
   */
  // Menyu ochadigan sahifa (masalan omborlar ro'yxati) ichki hisoblanmaydi.
  const inner = section !== undefined && pathname !== navEntry(section);

  const commands = useMemo<CommandItem[]>(
    () => [
      ...allowed.map((item) => {
        const Icon = item.icon;
        return {
          id: `nav:${item.to}`,
          label: t(item.label),
          // Manzilning o'zi eng qisqa izoh: yorliqni bir marta ko'rgan odam
          // keyingi safar to'g'ridan-to'g'ri uni yozadi.
          hint: navEntry(item),
          keywords: navEntry(item),
          icon: <Icon size={15} aria-hidden />,
          group: 'pages' as const,
          run: () => go(navEntry(item)),
        };
      }),
      {
        id: 'action:theme',
        label: t('command.toggleTheme'),
        icon: <SunMoon size={15} aria-hidden />,
        group: 'actions' as const,
        run: () => setThemeMode(nextThemeMode[themeMode]),
      },
      {
        id: 'action:sidebar',
        label: t('command.toggleSidebar'),
        icon: <PanelLeft size={15} aria-hidden />,
        group: 'actions' as const,
        run: toggleCollapsed,
      },
      {
        id: 'action:signature',
        label: t('signature.mine'),
        icon: <PenLine size={15} aria-hidden />,
        group: 'actions' as const,
        run: () => setSignatureOpen(true),
      },
      {
        id: 'action:logout',
        label: t('command.logout'),
        icon: <LogOut size={15} aria-hidden />,
        group: 'actions' as const,
        run: handleLogout,
      },
    ],
    [allowed, go, handleLogout, setThemeMode, themeMode, toggleCollapsed],
  );

  return (
    <div className="flex min-h-dvh bg-bg text-text">
      {mobile ? null : (
        <Sidebar
          can={canAnywhere}
          collapsed={collapsed}
          onToggle={toggleCollapsed}
          activePath={pathname}
          badges={badges}
          hasSubdivision={hasSubdivision}
          onNavigate={go}
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar
          sectionLabel={sectionLabel === undefined ? t('app.name') : t(sectionLabel)}
          onBack={inner ? () => { router.history.back(); } : undefined}
          onOpenPalette={() => setPaletteOpen(true)}
          userName={user?.fullName ?? null}
          userRole={user?.isSuperadmin === true ? t('topbar.roleAdmin') : t('topbar.roleStaff')}
          onLogout={handleLogout}
        />

        {/* Kenglik CHEGARALANMAYDI: ombor jadvallari, 3D sahna va hujjat
            satrlari keng ekranni to'liq ishlatishi kerak. Matn qatorining
            o'qilishi esa sahifaning O'ZIDA hal qilinadi (`max-w-prose`) —
            u yerda kontekst ma'lum, karkasda esa yo'q. */}
        {/* Pastki qator sahifa ustida turadi — oxirgi qator uning ostida
            qolib ketmasligi uchun shuncha bo'shliq qoldiriladi. */}
        <main
          className={cn(
            'w-full flex-1 px-3 py-5 sm:px-5',
            mobile
              ? 'pb-[calc(var(--size-tabbar)_+_env(safe-area-inset-bottom)_+_1.25rem)]'
              : undefined,
          )}
        >
          {/*
            Chegara AYNAN shu yerda — `<Outlet />` atrofida, qobiqning
            ichida. Sahifa yiqilsa faqat o'rtadagi qism yo'qoladi:
            navigatsiya, menyu va pastki qator joyida qoladi va odam
            boshqa bo'limga o'ta oladi. Yuqoriroqqa qo'yilsa, yiqilgan
            sahifa butun ilovani o'chirib, oq ekran qoldirardi.

            `resetKey` — joriy manzil: boshqa bo'limga o'tilganda eski
            xato ekranda qolib ketmasin.
          */}
          <ErrorBoundary resetKey={pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      {mobile ? (
        <>
          <MobileTabBar
            can={canAnywhere}
            activePath={pathname}
            badges={badges}
            hasSubdivision={hasSubdivision}
            onNavigate={go}
            onOpenMenu={() => setNavMenuOpen(true)}
          />

          <MobileNavDrawer
            open={navMenuOpen}
            onClose={() => setNavMenuOpen(false)}
            can={canAnywhere}
            activePath={pathname}
            badges={badges}
            hasSubdivision={hasSubdivision}
            onNavigate={go}
            userName={user?.fullName ?? null}
            userRole={user?.isSuperadmin === true ? t('topbar.roleAdmin') : t('topbar.roleStaff')}
            onOpenSignature={() => {
              setNavMenuOpen(false);
              setSignatureOpen(true);
            }}
            onLogout={handleLogout}
          />
        </>
      ) : null}

      <CommandPalette items={commands} open={paletteOpen} onOpenChange={setPaletteOpen} />

      <MySignatureDialog open={signatureOpen} onClose={() => setSignatureOpen(false)} />

      {/*
        Skaner — qobiqning bir qismi, sahifaniki emas: skladchi javon oldida
        turib etiketkani o'qiydi va qaysi ekranda ekani uni qiziqtirmaydi.
        Ekranda joy egallamaydi; o'z skaneri bor ekranlar (talabnoma berish,
        ombor bo'limidagi panel) uni band qiladi va bu jim turadi.
      */}
      <GlobalScanner />
    </div>
  );
};
